import Dexie, { type Table } from 'dexie'

import type {
  Budget,
  Categorie,
  Compte,
  Exception,
  MetaEntry,
  ModeleRepartition,
  Operation,
  Part,
  Piece,
  Recurrence,
  Reglement,
  Tiers,
} from '../domain/types'

/**
 * Base locale. Tout reste sur l'appareil : aucune synchronisation, aucun compte.
 *
 * ## Pourquoi aucun booleen n'est indexe
 *
 * IndexedDB n'accepte comme cle que : number, string, Date, ArrayBuffer et
 * Array. Un booleen n'est **pas une cle valide** : Dexie n'echoue pas, il omet
 * simplement l'enregistrement de l'index, et une requete sur ce champ renvoie
 * zero resultat sans le moindre message. Les drapeaux (`archive`, `actif`,
 * `pointee`) sont donc des `0 | 1`.
 *
 * ## Pourquoi presque rien n'est interroge par index
 *
 * `analysis/` travaille sur un instantane complet en memoire (voir
 * `donnees.ts`). Les index servent aux suppressions en cascade et aux
 * appariements ponctuels, pas aux calculs.
 *
 * ## Les pieces jointes a part
 *
 * Les blobs ne sont jamais charges avec le reste : une vingtaine de scans de
 * factures pese plus lourd que dix ans d'operations, et `chargerDonnees()` est
 * rappele a chaque modification.
 */
export class BudgetDb extends Dexie {
  comptes!: Table<Compte, string>
  categories!: Table<Categorie, string>
  tiers!: Table<Tiers, string>
  operations!: Table<Operation, string>
  recurrences!: Table<Recurrence, string>
  exceptions!: Table<Exception, string>
  modeles!: Table<ModeleRepartition, string>
  parts!: Table<Part, string>
  reglements!: Table<Reglement, string>
  budgets!: Table<Budget, string>
  pieces!: Table<Piece, string>
  meta!: Table<MetaEntry, string>

  constructor(nom = 'budget') {
    super(nom)

    this.version(1).stores({
      comptes: 'id, nom, archive',
      categories: 'id, nom, sens, parentId, modeleParDefautId',
      tiers: 'id, nom, type',
      operations: 'id, date, compteId, categorieId, recurrenceId, [compteId+date], [recurrenceId+occurrence]',
      recurrences: 'id, sens, compteId, categorieId, actif, modeleRepartitionId',
      exceptions: 'id, recurrenceId, [recurrenceId+occurrence]',
      modeles: 'id, nom',
      parts: 'id, operationId, tiersId',
      reglements: 'id, partId, date, compteId',
      budgets: 'id, categorieId, compteId, [categorieId+debut]',
      pieces: 'id, operationId',
      meta: 'cle',
    })
  }
}

export const db = new BudgetDb()

// ---------------------------------------------------------------------------
// Meta
// ---------------------------------------------------------------------------

export const META_REGLAGES = 'reglages'
export const META_MOI = 'moiId'
export const META_INITIALISEE = 'initialiseeLe'
export const META_DERNIER_EXPORT = 'dernierExportAt'

export async function lireMeta<T>(cle: string, defaut: T): Promise<T> {
  const ligne = await db.meta.get(cle)
  return ligne === undefined ? defaut : (ligne.valeur as T)
}

export async function ecrireMeta(cle: string, valeur: unknown): Promise<void> {
  await db.meta.put({ cle, valeur })
}

// ---------------------------------------------------------------------------

/**
 * Identifiant aleatoire.
 *
 * `crypto.randomUUID` n'existe que sur origine securisee : en developpement sur
 * l'IP du reseau local, en clair, il est absent. D'ou les deux replis.
 */
export function nouvelId(): string {
  const c = globalThis.crypto
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  if (c && typeof c.getRandomValues === 'function') {
    const o = c.getRandomValues(new Uint8Array(16))
    return Array.from(o, (n) => n.toString(16).padStart(2, '0')).join('')
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}
